import { create } from "@bufbuild/protobuf";
import { ArrowLeftIcon, ImageBrokenIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import { GetProjectRequestSchema } from "@gx/ts-contracts/services/ontology/api/project_pb";

import ErrorLayout from "@/layouts/ErrorLayout";
import FullScreenLoader from "@/layouts/FullScreenLoader";
import ProjectLayout from "@/layouts/project/ProjectLayout";

import { ProjectChatVariant } from "@/pages/projects/components/project/chat/constants";
import ProjectPageGraph from "@/pages/projects/components/project/ProjectPageGraph";
import { getProjectRefetchInterval } from "@/pages/projects/utils/projectRefetchInterval";

import { useGetProjectQuery } from "@/api/queries/projects";

const searchSchema = z.object({
  addProjectSources: z.coerce.boolean().optional(),
  chat: z.nativeEnum(ProjectChatVariant).catch(ProjectChatVariant.PANEL).optional(),
  chatId: z.string().optional(),
});

export const Route = createFileRoute("/_secure/projects/$projectId/graph/")({
  validateSearch: zodValidator(searchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = useParams({ from: "/_secure/projects/$projectId/graph/" });
  const { chat, chatId } = useSearch({ from: "/_secure/projects/$projectId/graph/" });
  const navigate = useNavigate();

  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery({
    input: create(GetProjectRequestSchema, { projectId }),
    options: {
      refetchInterval: (query) => getProjectRefetchInterval(query.state.data?.project),
    },
  });

  const handleGoToDashboard = () => {
    void navigate({ to: "/projects", search: {} });
  };

  if (isLoadingProject) {
    return <FullScreenLoader />;
  }

  if (!project?.project) {
    return (
      <ErrorLayout
        icon={ImageBrokenIcon}
        header="Project not found"
        message="The project you are looking for does not exist"
        actions={
          <Button
            label="Go to dashboard"
            icon={ArrowLeftIcon}
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={handleGoToDashboard}
          />
        }
      />
    );
  }

  return (
    <ProjectLayout project={project.project} chat={chat} chatId={chatId}>
      <ProjectPageGraph project={project.project} />
    </ProjectLayout>
  );
}
